import { clsx } from "clsx";
import {
  Check,
  ChevronsUpDown,
  CircleAlert,
  CircleCheck,
  Info,
  Search,
  TriangleAlert,
  X,
} from "lucide-react";
import { AnimatePresence, m } from "motion/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { Button } from "./Button";
import fieldStyles from "./Field.module.css";
import styles from "./Overlays.module.css";

// Toasts -----------------------------------------------------------------------------------

type ToastTone = "info" | "success" | "warning" | "danger";
type Toast = { id: number; tone: ToastTone; title: string; description?: string };
type ToastApi = { show: (toast: Omit<Toast, "id">) => void };

const ToastContext = createContext<ToastApi | null>(null);
const toastIcons: Record<ToastTone, ReactNode> = {
  info: <Info size={18} />,
  success: <CircleCheck size={18} />,
  warning: <TriangleAlert size={18} />,
  danger: <CircleAlert size={18} />,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);
  const dismiss = useCallback((id: number) => {
    setToasts((all) => all.filter((t) => t.id !== id));
  }, []);
  const api = useMemo<ToastApi>(
    () => ({
      show: (toast) => {
        counter.current += 1;
        const id = counter.current;
        setToasts((all) => [...all.slice(-3), { ...toast, id }]);
      },
    }),
    [],
  );
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={styles.toasts} role="region" aria-label="Notifications">
        <div aria-live="polite" aria-atomic="false" className={styles.toastList}>
          <AnimatePresence initial={false}>
            {toasts.map((toast) => (
              <ToastCard key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
            ))}
          </AnimatePresence>
        </div>
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const timer = window.setTimeout(onDismiss, toast.tone === "danger" ? 9000 : 5200);
    return () => window.clearTimeout(timer);
  }, [paused, onDismiss, toast.tone]);
  return (
    <m.div
      layout
      className={clsx(styles.toast, styles[`toast-${toast.tone}`])}
      initial={{ opacity: 0, y: 24, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 40, transition: { duration: 0.2 } }}
      transition={{ type: "spring", stiffness: 420, damping: 32 }}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
    >
      <span className={styles.toastIcon} aria-hidden="true">
        {toastIcons[toast.tone]}
      </span>
      <div className={styles.toastBody}>
        <p className={styles.toastTitle}>{toast.title}</p>
        {toast.description ? <p className={styles.toastText}>{toast.description}</p> : null}
      </div>
      <button type="button" className={styles.toastClose} onClick={onDismiss} aria-label="Dismiss">
        <X size={14} />
      </button>
    </m.div>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast needs a ToastProvider");
  return context;
}

// Confirm dialog ---------------------------------------------------------------------------

/** Native modal dialog: focus is trapped, Escape cancels, the page behind is inert. */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  tone = "danger",
  busy,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  tone?: "danger" | "primary";
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal?.();
    if (!open && dialog.open) dialog.close?.();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
      onClick={(event) => {
        if (event.target === ref.current && !busy) onClose();
      }}
    >
      <div className={styles.dialogCard}>
        <h2 id={titleId} className={styles.dialogTitle}>
          {title}
        </h2>
        <div className={styles.dialogText}>{children}</div>
        <div className={styles.dialogActions}>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant={tone} onClick={onConfirm} loading={busy} autoFocus>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </dialog>
  );
}

// Tabs -------------------------------------------------------------------------------------

export function Tabs<T extends string>({
  label,
  tabs,
  value,
  onChange,
}: {
  label: string;
  tabs: { value: T; label: string; icon?: ReactNode }[];
  value: T;
  onChange: (value: T) => void;
}) {
  const group = useId();
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const delta = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    const target =
      event.key === "Home" ? 0 : event.key === "End" ? tabs.length - 1 : index + delta;
    if (target === index && !delta) return;
    event.preventDefault();
    const next = tabs[(target + tabs.length) % tabs.length];
    if (!next) return;
    onChange(next.value);
    document.getElementById(`${group}-tab-${next.value}`)?.focus();
  };
  return (
    <div role="tablist" aria-label={label} className={styles.tabs}>
      {tabs.map((tab, index) => {
        const selected = tab.value === value;
        return (
          <button
            key={tab.value}
            id={`${group}-tab-${tab.value}`}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={`panel-${tab.value}`}
            tabIndex={selected ? 0 : -1}
            className={styles.tab}
            onClick={() => onChange(tab.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {tab.icon ? <span aria-hidden="true">{tab.icon}</span> : null}
            {tab.label}
            {selected ? (
              <m.span
                layoutId={`tab-underline-${group}`}
                className={styles.tabIndicator}
                transition={{ type: "spring", stiffness: 480, damping: 38 }}
              />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

// Combobox ---------------------------------------------------------------------------------

/** Most options rendered at once; a longer list is narrowed by typing. */
const MAX_SHOWN = 400;
const NONE: Record<string, string> = {};

/**
 * Pick from a list (WAI-ARIA 1.2 combobox with list autocomplete). Typing filters the options
 * by name and label. In strict mode only a listed option can be chosen, so typing searches;
 * otherwise the typed value is kept. Arrow keys move through the options, Enter picks, Escape
 * closes.
 */
export function Combobox({
  value,
  onChange,
  options,
  placeholder,
  badges = NONE,
  labels = NONE,
  strict = false,
  noun = "option",
  disabled = false,
  id,
  ...aria
}: {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  badges?: Record<string, string>;
  /** Secondary text shown under each option, and searched too (display names). */
  labels?: Record<string, string>;
  strict?: boolean;
  /** What the options are, for the count above a long list ("model"). */
  noun?: string;
  disabled?: boolean;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const term = (query ?? "").trim().toLowerCase();
  const filtered = useMemo(
    () =>
      term
        ? options.filter(
            (o) => o.toLowerCase().includes(term) || labels[o]?.toLowerCase().includes(term),
          )
        : options,
    [options, labels, term],
  );
  const shown = filtered.slice(0, MAX_SHOWN);
  const listed = open && !disabled && shown.length > 0;
  const noMatch = open && !disabled && strict && Boolean(term) && shown.length === 0;
  const counted = strict && options.length > 8;

  // Keep the active option in view while moving through a long list with the arrow keys.
  useEffect(() => {
    if (listed) document.getElementById(`${listId}-${active}`)?.scrollIntoView?.({ block: "nearest" });
  }, [listed, active, listId]);

  const openList = () => {
    setOpen(true);
    setActive(Math.max(0, query === null ? options.indexOf(value) : 0));
  };

  const close = () => {
    setOpen(false);
    setQuery(null);
  };

  const pick = (option: string) => {
    onChange(option);
    close();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        openList();
        return;
      }
      const delta = event.key === "ArrowDown" ? 1 : -1;
      setActive((i) => (shown.length ? (i + delta + shown.length) % shown.length : 0));
    } else if (event.key === "Enter" && open && (shown[active] !== undefined || strict)) {
      // While searching, Enter picks the active option and never submits the form.
      event.preventDefault();
      if (shown[active] !== undefined) pick(shown[active]);
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      close();
    }
  };

  const count = term
    ? `${filtered.length} of ${options.length} ${noun}s`
    : `${options.length} ${noun}s`;

  return (
    <div className={clsx(fieldStyles.control, styles.combo, strict && styles.comboStrict)}>
      {strict ? (
        <span className={fieldStyles.leading}>
          <Search size={16} aria-hidden="true" />
        </span>
      ) : null}
      <input
        ref={inputRef}
        id={id}
        role="combobox"
        aria-expanded={listed}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={listed && shown[active] !== undefined ? `${listId}-${active}` : undefined}
        className={styles.comboInput}
        value={query ?? value}
        placeholder={placeholder}
        disabled={disabled}
        spellCheck={false}
        autoComplete="off"
        onChange={(event) => {
          setQuery(event.target.value);
          if (!strict) onChange(event.target.value.trim());
          setActive(0);
          setOpen(true);
        }}
        onFocus={(event) => {
          openList();
          // Typing then searches afresh instead of editing the chosen value.
          if (strict) event.currentTarget.select();
        }}
        onBlur={() => window.setTimeout(close, 120)}
        onKeyDown={onKeyDown}
        {...aria}
      />
      <button
        type="button"
        tabIndex={-1}
        className={styles.comboToggle}
        aria-label={open ? "Hide the list" : "Show the list"}
        disabled={disabled}
        onMouseDown={(event) => {
          event.preventDefault();
          if (open) close();
          else if (document.activeElement === inputRef.current) openList();
          else inputRef.current?.focus();
        }}
      >
        <ChevronsUpDown size={16} aria-hidden="true" />
      </button>
      <AnimatePresence>
        {listed || noMatch ? (
          <m.div
            className={styles.comboPopup}
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, transition: { duration: 0.12 } }}
            transition={{ duration: 0.18 }}
          >
            {noMatch ? (
              <p className={styles.comboEmpty} role="status">
                No {noun}s match “{query?.trim()}”.
              </p>
            ) : counted ? (
              <p className={styles.comboCount} aria-hidden="true">
                {count}
                {filtered.length > MAX_SHOWN ? ` · type to narrow the list` : null}
              </p>
            ) : null}
            {listed ? (
              <ul id={listId} role="listbox" className={styles.comboList}>
                {shown.map((option, index) => (
                  <li
                    key={option}
                    id={`${listId}-${index}`}
                    role="option"
                    aria-selected={option === value}
                    data-active={index === active || undefined}
                    className={styles.comboOption}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      pick(option);
                    }}
                    onMouseEnter={() => setActive(index)}
                  >
                    <span className={styles.comboText}>
                      <span className="mono">{option}</span>
                      {labels[option] ? (
                        <span className={styles.comboLabel}>{labels[option]}</span>
                      ) : null}
                    </span>
                    {option === value ? (
                      <Check size={15} className={styles.comboCheck} aria-hidden="true" />
                    ) : null}
                    {badges[option] ? <span className={styles.comboBadge}>{badges[option]}</span> : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </m.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
